import type { ReactNode } from 'react';
import { Globe } from 'lucide-react';
import { EtiquetaDePagina } from '@/components/patterns';
import {
  CLASE_SUBTITULO_DE_PAGINA,
  CLASE_TITULO_DE_PAGINA,
} from '@/components/patterns/page-header';
import { cn } from '@/lib/utils/cn';
import { usd, sobreElFob } from '../utils/formatos';

/**
 * El encabezado de las pantallas de Comercia (maqueta de Melanie): la píldora
 * naranja del módulo, el título grande y, a la derecha, el costo acumulado de la
 * importación que se está armando.
 */
export function EncabezadoDeComercia({
  etiqueta = 'Comercia',
  titulo,
  subtitulo,
  acumulado,
  children,
}: {
  etiqueta?: string;
  titulo: ReactNode;
  subtitulo?: ReactNode;
  /** `null` mientras no hay importación: la tarjeta no se dibuja. */
  acumulado: { total: number; fob: number; moneda?: string } | null;
  /** Lo que va debajo del título (las pestañas de Productos). */
  children?: ReactNode;
}) {
  return (
    <header className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
        <div className="min-w-0 space-y-3 pt-2">
          <EtiquetaDePagina icono={Globe} modulo="comercia">
            {etiqueta}
          </EtiquetaDePagina>
          <h1
            className={cn(
              CLASE_TITULO_DE_PAGINA,
              'font-titulo text-[32px] leading-[1.08] sm:text-[40px]'
            )}
          >
            {titulo}
          </h1>
          {subtitulo && <p className={CLASE_SUBTITULO_DE_PAGINA}>{subtitulo}</p>}
        </div>
        {acumulado && <TarjetaCostoAcumulado {...acumulado} />}
      </div>
      {children}
    </header>
  );
}

/**
 * «COSTO ACUMULADO · USD 46.300 · vs FOB USD 46.300 · +0,0 %»: lo que lleva
 * costando la importación hasta el paso en que se está. Arranca en el FOB y suma
 * la logística al elegir la ruta y los tributos en Costo nacionalizado.
 */
export function TarjetaCostoAcumulado({
  total,
  fob,
  moneda = 'USD',
}: {
  total: number;
  fob: number;
  moneda?: string;
}) {
  const sube = fob > 0 ? ((total - fob) / fob) * 100 : 0;
  return (
    <section
      aria-label="Costo acumulado de la importación"
      className="min-w-[264px] rounded-tarjeta bg-degradado-prixia px-8 py-5 text-white shadow-[0_18px_40px_-18px_rgba(93,33,179,0.75)]"
    >
      <p className="text-xs font-bold tracking-[0.14em]">COSTO ACUMULADO</p>
      <p className="mt-1 font-titulo text-[38px] font-semibold leading-none tabular-nums tracking-[-0.02em]">
        {usd(total, moneda)}
      </p>
      <p className="mt-2 text-xs font-medium text-white/85 tabular-nums">
        vs FOB {usd(fob, moneda)} · {sobreElFob(sube)}
      </p>
    </section>
  );
}
